import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Forum as ForumIcon,
  Notifications as NotificationsIcon,
  Assessment as AssessmentIcon,
  Campaign as CampaignIcon,
  Public as PublicIcon,
  Email as EmailIcon,
  Policy as PolicyIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const CSMSSystemDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/pi-planning');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to PI Planning Board"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            Cargo Systems Messaging Service (CSMS)
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            CBP Trade Community Communication and Notification Platform
          </Typography>
        </Box>
      </Box>

      {/* System Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            System Overview
          </Typography>
          <Typography variant="body1" paragraph>
            The Cargo Systems Messaging Service (CSMS) is U.S. Customs and Border Protection's official communication 
            platform for broadcasting operational updates, policy changes, system maintenance notices, and critical alerts 
            to the international trade community. CSMS serves as the authoritative source for time-sensitive information 
            affecting importers, exporters, customs brokers, carriers, and other trade stakeholders.
          </Typography>
        </CardContent>
      </Card>

      {/* 5Ws Summary */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Overview: The 5Ws
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="CSMS System 5Ws Overview">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '15%', bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="What is CSMS"
                >
                  What
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    A <strong>broadcast messaging system</strong> that disseminates official CBP communications to the trade 
                    community. CSMS delivers <strong>policy updates, operational changes, system outages, and critical alerts</strong> 
                    via email distribution lists. Serves as the <strong>authoritative source</strong> for ACE system status, 
                    regulatory changes, and trade program announcements.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Who uses CSMS"
                >
                  Who
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Managed by <strong>CBP Office of Trade</strong> and subscribed to by the international trade community including 
                    <strong> importers, exporters, customs brokers, freight forwarders, carriers, software vendors, and trade 
                    associations</strong>. Over <strong>75,000 subscribers</strong> receive CSMS messages. Messages are also posted 
                    to the <strong>CBP.gov CSMS archive</strong> for public access.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="When was CSMS deployed"
                >
                  When
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Established in the <strong>1990s</strong> as the primary communication channel for ACS/ACE system updates. 
                    Modernized with <strong>web-based subscription management (2010)</strong> and <strong>searchable message 
                    archive (2015)</strong>. Messages are distributed <strong>24/7</strong> as events occur, with scheduled 
                    maintenance notices typically sent <strong>72 hours in advance</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Where is CSMS deployed"
                >
                  Where
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>Email-based distribution system</strong> accessible worldwide to registered subscribers. Messages are 
                    archived on <strong>CBP.gov/CSMS</strong> for public access. Subscription management available through 
                    <strong> CBP.gov online portal</strong>. Messages reach subscribers <strong>globally</strong> within minutes 
                    of publication, supporting international trade operations.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Why CSMS exists"
                >
                  Why
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    To provide <strong>timely, authoritative communication</strong> that enables the trade community to maintain 
                    compliance and operational continuity. Ensures <strong>transparency</strong> in CBP operations, reduces 
                    confusion from unofficial sources, and enables <strong>proactive planning</strong> for system changes and 
                    policy updates. Critical for <strong>business continuity</strong> during system outages or emergency situations.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Message Categories */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <ForumIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        CSMS Message Categories
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PolicyIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Policy Change Notifications
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Official announcements of regulatory and policy changes affecting trade operations.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Regulatory Updates:</strong> Changes to customs regulations and procedures</Typography></li>
                <li><Typography variant="body2"><strong>Tariff Changes:</strong> HTS updates and duty rate modifications</Typography></li>
                <li><Typography variant="body2"><strong>Trade Programs:</strong> C-TPAT, ISF, and other program changes</Typography></li>
                <li><Typography variant="body2"><strong>PGA Requirements:</strong> Partner agency data element updates</Typography></li>
                <li><Typography variant="body2"><strong>Enforcement Actions:</strong> New WROs, AD/CVD cases, IPR enforcement</Typography></li>
                <li><Typography variant="body2"><strong>Implementation Dates:</strong> Effective dates for new requirements</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <NotificationsIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Operational Updates
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Real-time information on system operations and port-specific changes.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>System Status:</strong> ACE/ABI availability and performance issues</Typography></li>
                <li><Typography variant="body2"><strong>Port Operations:</strong> Port closures, hour changes, special procedures</Typography></li>
                <li><Typography variant="body2"><strong>Processing Delays:</strong> Backlogs and extended processing times</Typography></li>
                <li><Typography variant="body2"><strong>Emergency Procedures:</strong> Hurricane, pandemic, or crisis protocols</Typography></li>
                <li><Typography variant="body2"><strong>Cargo Holds:</strong> Widespread hold situations and resolution guidance</Typography></li>
                <li><Typography variant="body2"><strong>Exam Backlogs:</strong> Inspection delays and alternative procedures</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CampaignIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Trade Community Alerts
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Important announcements and reminders for the trade community.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Deadline Reminders:</strong> Filing deadlines and compliance dates</Typography></li>
                <li><Typography variant="body2"><strong>Training Opportunities:</strong> Webinars and educational events</Typography></li>
                <li><Typography variant="body2"><strong>Stakeholder Meetings:</strong> COAC, trade symposiums, listening sessions</Typography></li>
                <li><Typography variant="body2"><strong>Fraud Alerts:</strong> Scams targeting trade community</Typography></li>
                <li><Typography variant="body2"><strong>Best Practices:</strong> Guidance on compliance and efficiency</Typography></li>
                <li><Typography variant="body2"><strong>Success Stories:</strong> Trade facilitation program highlights</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmailIcon sx={{ fontSize: 40, color: '#D32F2F', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                  System Maintenance Notices
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Scheduled and emergency maintenance notifications for ACE and related systems.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Planned Outages:</strong> Scheduled maintenance windows (typically weekends)</Typography></li>
                <li><Typography variant="body2"><strong>System Upgrades:</strong> New feature deployments and enhancements</Typography></li>
                <li><Typography variant="body2"><strong>Emergency Maintenance:</strong> Unplanned outages and recovery updates</Typography></li>
                <li><Typography variant="body2"><strong>Performance Issues:</strong> Slowdowns and degraded service notifications</Typography></li>
                <li><Typography variant="body2"><strong>Restoration Updates:</strong> Service recovery progress and completion</Typography></li>
                <li><Typography variant="body2"><strong>Contingency Procedures:</strong> Alternative filing methods during outages</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* System Statistics */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Reach & Impact
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                75K+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Subscribers
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Trade community members
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                500+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Annual Messages
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Average per year
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                &lt;5min
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Delivery Time
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                From publication to inbox
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                24/7
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Message Distribution
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Critical alerts anytime
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Benefits */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Trade Community Benefits
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Authoritative Source:</strong> Official CBP communications eliminate confusion</Typography></li>
                <li><Typography variant="body2"><strong>Timely Notifications:</strong> Immediate awareness of critical changes</Typography></li>
                <li><Typography variant="body2"><strong>Proactive Planning:</strong> Advance notice enables business preparation</Typography></li>
                <li><Typography variant="body2"><strong>Compliance Support:</strong> Stay current with regulatory requirements</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Business Continuity:</strong> Contingency procedures during disruptions</Typography></li>
                <li><Typography variant="body2"><strong>Transparency:</strong> Clear communication of CBP operations and changes</Typography></li>
                <li><Typography variant="body2"><strong>Searchable Archive:</strong> Historical reference for past announcements</Typography></li>
                <li><Typography variant="body2"><strong>Free Service:</strong> No cost for subscription or message access</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Subscription Information */}
      <Card sx={{ mb: 4, bgcolor: '#fff3e0', border: '2px solid #F57C00' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#F57C00' }}>
            How to Subscribe to CSMS
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Subscription Options:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Visit CBP.gov/CSMS subscription page</Typography></li>
                <li><Typography variant="body2">Select message categories of interest</Typography></li>
                <li><Typography variant="body2">Provide email address for notifications</Typography></li>
                <li><Typography variant="body2">Confirm subscription via email link</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Message Categories:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">ACE System Messages (all users)</Typography></li>
                <li><Typography variant="body2">ABI Messages (brokers and filers)</Typography></li>
                <li><Typography variant="body2">e-Manifest (carriers and NVOCCs)</Typography></li>
                <li><Typography variant="body2">Trade Policy Updates (all stakeholders)</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Message Archive */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            CSMS Message Archive
          </Typography>
          <Typography variant="body2" paragraph>
            All CSMS messages are archived on CBP.gov for public access and historical reference:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Chip label="Searchable by Date" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Find messages by publication date</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="Searchable by Keyword" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Search message content</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="Searchable by Number" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Reference specific CSMS numbers</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="PDF Download" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Download messages for records</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="RSS Feed Available" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Automated feed integration</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="Multi-Year Archive" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Historical messages retained</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default CSMSSystemDetail;
